/**
 * Engine-hosted sales page for paid programs.
 *
 * Route: /programs/:slug
 *
 * Data sources:
 *   GET /api/public/programs/:slug → program metadata + resolved brand tokens
 *
 * Renders a long-form, conversion-oriented sales page using the program's
 * branding palette via CSS custom properties. CTAs trigger an inline buyer
 * intent capture; on submit the visitor navigates to /programs/:slug/order
 * where the Spiffy checkout embed lives.
 *
 * Phase 1 (this file): Clients program. Copy mirrors the v3 HTML mockup.
 * All Clients-specific copy is encapsulated in CLIENTS_CONTENT below
 * so this component can later become slug-driven.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BRAND_NAME } from './branding';
import { recordLandingView } from './api';
import { HeroBackgroundLayer } from './components/HeroBackground';
import ProgramRsvpView from './ProgramRsvpView';
import MembershipSalesPage, { MEMBERSHIP_CONTENT } from './MembershipSalesPage';
import SodaIntensivePage from './SodaIntensivePage';
import BkosLicensePage from './BkosLicensePage';
import FreedomGatewayPage from './FreedomGatewayPage';
import { useRegionRedirect } from './hooks/useRegionRedirect';
import { PolicyLinks } from './components/PolicyLinks';
import { fetchWithRetry } from './lib/fetchRetry';
import { useBrandGtm } from './lib/useBrandGtm';

const API = import.meta.env.VITE_API_BASE_URL as string;

// ────────────────────────────────────────────────────────────────────────────
// API response types
// ────────────────────────────────────────────────────────────────────────────

export interface ProgramPublicResponse {
  program: {
    slug: string;
    name: string;
    type: string;
    display_name?: string | null;
    description?: string | null;
  };
  brand: {
    id: string;
    slug: string;
    name: string;
    palette: Record<string, string>;
    fonts: Record<string, unknown> | null;
    logoUrl: string | null;
    logoWhiteUrl: string | null;
    faviconUrl: string | null;
    gtmContainerId?: string | null;
  } | null;
  tokens: {
    palette: Record<string, string> | null;
    fonts: Record<string, unknown> | null;
  };
  commerce?: {
    currency: string;
    checkoutUrl: string | null;
    plans: Array<{
      id?: string;
      label?: string;
      total?: number;
      per_payment?: number;
      payments?: number;
      savings?: number;
      highlight?: boolean | number;
      /** Multi-tier programs (e.g. Freedom Gateway Solo / Ultimate) route each
       *  tier to its own Spiffy product. When set, the order page uses this in
       *  preference to commerce.checkoutUrl. */
      checkoutUrl?: string;
      /** Spiffy product code for fulfillment sku_match (e.g. 'tsb-fgp-solo'). */
      sku?: string;
    }>;
    cohortStarts: string | null;
    enrollmentCloses: string | null;
    /** Server-composed footnote in the resolved currency, e.g. "All prices in
     *  CAD." or "All prices in AUD. GST included." */
    taxNote?: string;
  } | null;
  /** Resolved region (migration 0163) — the bespoke landing renders variant
   *  content (coach, dates, local time, currency, tier cards) from `profile`. */
  region?: {
    code: string;
    cohortId: string;
    isDefault: boolean;
    profile: Record<string, any> | null;
  } | null;
  /** All switchable regional cohorts, for region links / geo auto-route. */
  regions?: Array<{ code: string; label: string; isDefault: boolean; countries: string[] }>;
  googleFontsUrl: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Clients program content
// ────────────────────────────────────────────────────────────────────────────

const CLIENTS_CONTENT = {
  eyebrow: 'Clients — Enrollment open',
  // Date-free fallback only — shown while the live breakout date is loading
  // or if the cohort-stats fetch fails. The real value is resolved at render
  // time from GET /api/programs/:slug/cohort-stats (see kickoffWhen state
  // below) — never hardcoded here. See sop-ee-clients-evergreen.
  whenLine: 'Enrollment open — register today',
  h1: 'The clients you want are out there. They just don\'t know you exist yet.',
  heroSub:
    'Clients is a 12-month program for bookkeeping business owners who are ready to build a practice on their terms. The first 90 days are instruction and implementation — Lisa\'s twelve modules, one a week, and the work to put each into your business. The next nine months are execution and support — taking it to market every week with your coaching team, Lisa Campbell, Teresa Slack, and Jennifer Hume, beside you. So that by the time you\'re done, winning the right clients isn\'t something you\'re working toward. It\'s just how your business works.',

  quickstats: [
    { value: '90 days', label: 'instruction & implementation' },
    { value: '9 months', label: 'execution & support' },
    { value: '6-person', label: 'coaching team' },
    { value: '43,000+', label: 'bookkeepers researched' },
  ],

  painIntro:
    'Being great at bookkeeping and being great at getting clients are two completely different skills. One you\'ve spent your career developing. The other? Most bookkeepers are winging it — and our data from 43,000+ contacts makes that very clear.',

  pains: [
    {
      lead: 'Your pipeline is unpredictable — busy one month, dry the next.',
      proof:
        '"Gaining new clients" is the #1 challenge — cited by 67% of bookkeepers in our database, above systems, pricing, and team',
    },
    {
      lead: 'You\'re taking clients you don\'t love because you\'re afraid to say no.',
      proof:
        '"Not having clients that pay us what our level of service and professionalism is worth" — VOC, Strategy Prep forms',
    },
    {
      lead: 'You feel invisible — you know you\'re good, but you can\'t explain what makes you different.',
      proof: null,
    },
    {
      lead: 'Your discovery calls aren\'t converting, and you can\'t quite tell why.',
      proof: null,
    },
    {
      lead: 'When prospects ask about pricing, you fold — or you talk yourself out of the rate you deserve.',
      proof: null,
    },
    {
      lead: 'Referrals come in waves you can\'t predict and can\'t replicate.',
      proof: null,
    },
  ],
  painClose:
    'Here\'s the truth: the bookkeepers winning right now aren\'t smarter than you. They have a system — and a team in their corner who\'s done this before.',

  shifts: [
    {
      phase: 'Phase 1',
      h3: 'Selective & Profitable',
      body: 'You know exactly who your right-fit client is, you have the filters to spot them on the first call, and you stop saying yes to people who drain your business.',
      bullets: [
        'Ideal client profile (locked in)',
        'Intake + qualification system',
        'Discovery process that filters fast',
        'Onboarding that protects your standards',
      ],
    },
    {
      phase: 'Phase 2',
      h3: 'Consistently Booked',
      body: 'Your calendar fills with discovery calls from the right people — not because you got lucky, but because the system runs on its own and brings them to you.',
      bullets: [
        'Outreach system in motion',
        'Accountant & CPA partnerships',
        'Referral & testimonial engine',
        'Pipeline + nurture sequence',
      ],
    },
    {
      phase: 'Phase 3',
      h3: 'Known & Referred',
      body: 'You stop being the best-kept secret in your market. You have a clear position, a story people repeat, and a referral network that talks about you when you\'re not in the room.',
      bullets: [
        'Clarified positioning & message',
        'Visibility system that compounds',
        'Referral network you actively nurture',
        'Authority content that does the selling',
      ],
    },
  ],

  journeyInstallTitle: 'Months 1–3 · Instruction & Implementation',
  journeyInstallHeadline: 'Build the system. Lock in the foundation.',
  journeyInstallSub:
    'Lisa\'s twelve modules — one a week, yours to work through. By the time the final module lands, the entire client acquisition system is built and in your hands.',
  journeyInstallBullets: [
    'All 12 modules — Lisa\'s full curriculum, one a week',
    'Workbooks & exercises for every module',
    'Frameworks, templates, scripts, checklists',
    'Weekly group coaching while you build',
    'Your ideal client, offer, pitch, and pipeline locked in',
    'By the final module: you have everything you need to start winning',
  ],
  journeyExecTitle: 'Months 4–12 · Execution & Support',
  journeyExecHeadline: 'Execute. Execute. Execute — with your team.',
  journeyExecSub:
    'This is where most programs leave you. Where ours kicks in. Nine months of real-world execution support as you take the system into the market and onboard the clients you actually want.',
  journeyExecBullets: [
    'Ongoing coaching with Lisa & the team',
    'Sales role-plays for live discovery calls',
    'Pricing & proposal review on every new prospect',
    'Mindset support when the doubts creep back in',
    'Onboarding help as right-fit clients come on board',
    'A network of bookkeepers building right alongside you',
  ],
  journeyCallout:
    'Idea + strategy doesn\'t fry the fish. You need execution and accountability — and that\'s exactly what the next twelve months deliver.',

  team: [
    {
      role: 'Program Host',
      name: 'Lisa Campbell',
      bio: 'Profit strategist, best-selling author of Beyond Bookkeeping, and founder of The Marcam Group. Lisa designed Clients from the methodology she used to grow her own multi-six figure firm — and has spent a decade coaching bookkeepers through it.',
      photo:
        'https://images.thesuccessfulbookkeeper.com/cdn-cgi/image/width=800,format=auto/host/c87e98b2-e099-4753-92f1-1631dc792478/cutout.png',
    },
    {
      role: 'Sales & Marketing Coach',
      name: 'Teresa Slack',
      bio: 'Built and sold a seven-figure bookkeeping firm with her sister Connie. Teresa knows what works at every revenue stage — she\'s done it. She leads the weekly Success Blueprint call on sales, marketing, and pricing topics.',
      photo:
        'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/people/teresa-slack/photo-default.jpg',
    },
    {
      role: 'Team & Operations Coach',
      name: 'Jennifer Hume',
      bio: 'When new right-fit clients start arriving and you need help building the team to serve them, Jennifer is the one. She specializes in team-building, hiring, and managing — the systems that let you grow without breaking.',
      photo:
        'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/people/jennifer-hume/photo-default.jpg',
    },
    {
      role: 'Sales & ROI Coach',
      name: 'Debra Angilletta',
      bio: 'The queen of sales. Debra can find the ROI in any prospect conversation — even when you can\'t see it yourself. She runs role-plays that pull you out of your shell and teach you how to lead a discovery call instead of being led by one.',
      photo:
        'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/people/debra-angilletta/photo-default.jpg',
    },
    {
      role: 'Mindset Mentor',
      name: 'Steve Kushman',
      bio: 'More than 20 years supporting Special Olympics athletes, Steve teaches people to focus on what they can control, quiet the inner critic, and take the next step rather than fixate on the finish line. A 2018 Kilimanjaro summiteer, he leads with relentless optimism — and helps bookkeepers win the war between their ears.',
      photo:
        'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/people/steve-kushman/photo-default.png',
    },
    {
      role: 'Client Experience',
      name: 'Ron Bester',
      bio: 'Your point person inside the program. Ron makes sure you have access to every resource, every replay, every coaching call, and every team member when you need them. He\'s the one removing friction so you stay focused on execution.',
      photo:
        'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/people/ron-bester/photo-default.png',
    },
  ],

  modules: [
    {
      num: 'M01',
      phase: 'Clarity',
      title: 'Your Ideal Client',
      desc: 'Define exactly who you serve — the people who pay your rates, respect your work, and refer their friends.',
    },
    {
      num: 'M02',
      phase: 'Clarity',
      title: 'Your Position & Message',
      desc: 'Stop sounding like every other bookkeeper. Build the positioning that makes you obviously different — and obviously the right choice.',
    },
    {
      num: 'M03',
      phase: 'Clarity',
      title: 'Your Offer',
      desc: 'Package your services so they\'re easy to say yes to, easy to refer, and impossible to compare on price alone.',
    },
    {
      num: 'M04',
      phase: 'Visibility',
      title: 'Outreach & Authority',
      desc: 'Get in front of the right people consistently — without it feeling like cold sales or scrolling LinkedIn for hours.',
    },
    {
      num: 'M05',
      phase: 'Visibility',
      title: 'Accountant & CPA Partnerships',
      desc: 'Build the partner relationships that send you qualified, pre-sold referrals on a regular basis.',
    },
    {
      num: 'M06',
      phase: 'Visibility',
      title: 'Referral & Testimonial Engine',
      desc: 'Stop waiting for referrals. Install the system that asks for them, captures them, and turns happy clients into your best salespeople.',
    },
    {
      num: 'M07',
      phase: 'Conversion',
      title: 'Your Client Pipeline',
      desc: 'Build the pipeline view that lets you see exactly where every prospect is — and the next move to close them.',
    },
    {
      num: 'M08',
      phase: 'Conversion',
      title: 'Discovery That Converts',
      desc: 'Lead the call instead of being led. The framework that turns "I\'ll think about it" into "When can we start?"',
    },
    {
      num: 'M09',
      phase: 'Conversion',
      title: 'Pricing & Proposals',
      desc: 'Walk into pricing conversations with confidence — and walk out with the rates you deserve, not the ones you settled for.',
    },
    {
      num: 'M10',
      phase: 'Retention',
      title: 'Onboarding That Sticks',
      desc: 'First 100 days set the tone for the next 10 years. Install the onboarding that turns new clients into raving fans from day one.',
    },
    {
      num: 'M11',
      phase: 'Retention',
      title: 'Client Success & Renewals',
      desc: 'Keep the right clients longer, charge them more over time, and never lose them on a competitor\'s discount.',
    },
    {
      num: 'M12',
      phase: 'Retention',
      title: 'Your Growth Engine',
      desc: 'Put it all together. Run the system on repeat. The same approach that takes you from where you are now to wherever you want to go next.',
    },
  ],

  moduleStrip: [
    { num: '12', label: 'Live modules with Lisa' },
    { num: '12', label: 'Workbooks & exercises' },
    { num: '40+', label: 'Templates, scripts & assets' },
    { num: '∞', label: 'Lifetime access to recordings' },
  ],

  includedCards: [
    {
      tag: 'Full Curriculum · 90 Days',
      h3: 'The full 12-module course, yours to complete',
      body: 'Twelve modules built by Lisa — your first 90 days of instruction and implementation, one a week. Each walks you through the framework, the exercises, and what to install in your business that week. All recorded for lifetime replay.',
      bullets: null as string[] | null,
    },
    {
      tag: 'Group Coaching · 12 Months',
      h3: 'Weekly group coaching for a full year',
      body: 'Multiple coaching touchpoints every week with the team. Bring your real prospects, your real proposals, your real objections. Get the input you need to take action by Friday.',
      bullets: [
        'Wednesday group coaching with your team — Lisa, Teresa & Jennifer',
        'Friday Success Blueprint with Teresa Slack',
        "Jennifer Hume's team-building call",
      ] as string[] | null,
    },
    {
      tag: 'Specialist Coaches',
      h3: '1:1 access to specialist coaches',
      body: "When you need sales role-plays, mindset work, or pricing strategy, you don't wait. The team is in your corner for as long as it takes — and they've coached hundreds of bookkeepers through what you're facing right now.",
      bullets: null as string[] | null,
    },
    {
      tag: 'Templates & Assets',
      h3: 'The complete toolkit',
      body: "Every script, template, checklist, and framework Lisa uses with her own firm and her highest-paying coaching clients. Built for bookkeepers — not adapted from another industry and hoped to fit.",
      bullets: [
        'Discovery call framework & scripts',
        'Pricing & proposal templates',
        'Onboarding checklists',
        'Email sequences & outreach assets',
      ] as string[] | null,
    },
    {
      tag: 'Community',
      h3: 'The cohort and the community',
      body: "You're building alongside other bookkeepers doing the same thing. Share what's working. Solve problems together. Find your people — the ones who get it.",
      bullets: null as string[] | null,
    },
  ],

  fitYes: [
    'More profit — and clients who actually pay it without complaint',
    'Better clients — the ones who respect your time and refer their friends',
    'Fewer PITA clients (you know exactly who we mean)',
    'A predictable pipeline instead of feast-or-famine months',
    'A team in your corner when the doubt creeps in or the prospect goes silent',
    'To stop feeling invisible in a market where you should be the obvious choice',
  ],
  fitNo: [
    "You haven't launched your bookkeeping business yet — start with the basics first",
    "You're not actually willing to put in the work between sessions",
    'You want a course you can buy and ignore — this is a live coaching program with a team alongside you',
  ],
  fitClose:
    "If you're already running a bookkeeping business and you want to do better in the area of clients, this is for 99% of you.",

  stats: [
    { num: '43,000+', label: 'Bookkeepers in our research base' },
    { num: '67%', label: 'Cite client acquisition as their #1 challenge' },
    { num: '2x', label: 'Revenue aspiration regardless of starting point' },
    {
      num: '6',
      label: 'Specialists in sales, mindset, systems, and client acquisition — in your corner for all 12 months',
    },
  ],

  lisaPhoto:
    'https://images.thesuccessfulbookkeeper.com/cdn-cgi/image/width=800,format=auto/host/c87e98b2-e099-4753-92f1-1631dc792478/cutout.png',
  lisaH2: 'I built this because I lived it',
  lisaLabel: 'A Note From Lisa',
  lisaCopy: [
    'I built this program because I had to figure all of this out the hard way myself.',
    'I was a bookkeeper. Three kids. A bad marriage. Drowning in debt and exhausted from chasing the wrong clients at the wrong rates.',
    'I was great at bookkeeping. I was terrible at running a business that got me the right clients. When I finally figured that out, everything changed. I paid off that debt within two years. I built The Marcam Group into a high multi-six figure firm. And I stopped working with clients who didn\'t value what I brought.',
    "That's what I want for you. Not just more clients — the right ones. The ones who respect your work, pay your rates, and refer you to people just like them. And the team I've built around this program? They're the people I wish I'd had when I was figuring this out alone.",
  ],
  lisaCreds: [
    'CEO, The Successful Bookkeeper',
    'Founder, The Marcam Group',
    'National Director, Profit First Professionals Canada',
    'Author, Beyond Bookkeeping',
    '10+ years coaching bookkeepers',
  ],

  pricingContext:
    "Most bookkeepers are already paying for the wrong clients — in time, energy, and rates that don't reflect their work. One right client who stays, pays well, and refers others can return the cost of this program many times over. It's just what happens when you stop taking everyone and start choosing the right ones.",

  pricingIncludes: [
    'Twelve modules of instruction & implementation — Lisa\'s full curriculum, one a week',
    'Weekly group coaching calls all year — Lisa, Teresa & Jennifer',
    'Monthly Sales Success call with Debra Angilletta',
    'Monthly Motivational Mindset call with Steve Kushman',
    'Complete Clients course curriculum — 12 modules',
    'Scripts, templates, frameworks & checklists',
    'Lifetime access to all recordings & materials',
  ],

  faqs: [
    {
      q: 'Is this really 12 months? What happens after the first 90 days?',
      a: "Yes — 12 full months, in two phases. The first 90 days are instruction and implementation: you work through Lisa's twelve modules, one a week, and build the system. The remaining nine months are execution and support — you take what you've built into the market with the full coaching team beside you. This is where most programs leave you. It's where we just get started.",
    },
    {
      q: "Who's actually coaching me through this?",
      a: 'Your Clients coaching team is Lisa Campbell, Teresa Slack, and Jennifer Hume — they run the weekly group coaching calls together (Teresa and Jennifer week to week, Lisa often on the call), and host a newbie breakout at the start of your intake. Lisa created the program and its curriculum. You also get coaching from Debra Angilletta (sales & ROI conversations) and Steve Kushman (mindset shifts). Ron Bester is your point of contact for any program access or resource questions.',
    },
    {
      q: "What's the weekly time commitment?",
      a: "The first 90 days are instruction and implementation — one module a week, at your own pace, plus the time to put it to work. Roughly 2–4 hours a week. From there, the next nine months are execution and support: you bring real prospects and proposals to the weekly group coaching call. The weekly calls run the full twelve months, so you attend the ones that apply to where you are right now. It's designed for working bookkeepers, not people with free calendars.",
    },
    {
      q: 'How is this different from networking or running ads?',
      a: "Networking and ads are tactics. This program installs a system that doesn't depend on whether you happen to feel like networking that week. You'll come out of 12 months with documented processes for getting clients, selecting them, onboarding them, and getting referred by them — things that keep working whether you do them or your future team does. Random tactics don't scale. Systems do.",
    },
    {
      q: 'Is there a payment plan?',
      a: 'Pay in full at {PIF} {CUR} (saving you {SAVINGS}), or spread it over 12 months at {MONTHLY} {CUR} per month. Same program, same access, same results either way.',
    },
    {
      q: 'Do I need to have completed another TSB program first?',
      a: "No. Clients can be your entry point if your bookkeeping business is already up and running. If your systems are still being built (Pure Bookkeeping, processes, workflow), Freedom Gateway is a great parallel path — but it's not a blocker. Talk to our team if you're not sure.",
    },
    {
      q: 'What happens at the end of the 12 months?',
      a: 'You keep lifetime access to every module, every recording, and every template. And when you\'re ready to go deeper — Pricing and Scale are the next chapters.',
    },
  ],

  finalHeadline: 'Stop being the best-kept secret in your market. Let\'s change that.',
  // Date-free — enrollment is always open, so this never names a start date.
  // The live breakout line (kickoffWhen) is appended separately at render.
  finalSub:
    'Enrollment is open — register today and get started right away. Ninety days of instruction and implementation, then nine months of execution and support — a full year with your coaching team.',
  footerMeta:
    '90 days instruction & implementation, then 9 months execution & support · {PIF} {CUR} pay in full or {MONTHLY} {CUR}/month × 12 · Coaching team: Lisa, Teresa & Jennifer',
};


// Slugs with full long-form sales content. Everything else that exists on
// the engine renders the ProgramRsvpView waitlist shell. (Freedom Gateway is
// NOT here — it has a dedicated bespoke emerald/gold page, FreedomGatewayPage,
// reached via the slug delegation in the render path below.)
const SALES_CONTENT: Record<string, typeof CLIENTS_CONTENT> = {
  clients: CLIENTS_CONTENT,
};

// ────────────────────────────────────────────────────────────────────────────
// Apply program palette + fonts to <html> as CSS custom properties
// ────────────────────────────────────────────────────────────────────────────

const CSS_PROP_CLEANUP: string[] = [];

function applyTokensToDom(data: ProgramPublicResponse): void {
  const root = document.documentElement;
  const palette = data.tokens?.palette;
  const fonts = data.tokens?.fonts;

  const set = (prop: string, val: string | null | undefined) => {
    if (val != null) {
      root.style.setProperty(prop, val);
      CSS_PROP_CLEANUP.push(prop);
    }
  };

  if (palette) {
    set('--prog-text-dark', palette.textDark);
    set('--prog-deep', palette.deep ?? palette.textDark);
    set('--prog-primary', palette.primary);
    set('--prog-bright', palette.bright ?? palette.primary);
    set('--prog-tint', palette.tint);
    set('--prog-canvas', palette.canvas);
    set('--prog-highlight', palette.highlight ?? null);
  }

  const heading = (fonts as any)?.heading as { family?: string } | undefined;
  const body = (fonts as any)?.body as { family?: string } | undefined;
  const display = (fonts as any)?.display as { family?: string } | undefined;
  set('--font-heading', heading?.family ? `'${heading.family}', sans-serif` : null);
  set('--font-body', body?.family ? `'${body.family}', sans-serif` : null);
  set('--font-display', display?.family ? `'${display.family}', serif` : null);
}

function cleanupTokensFromDom(): void {
  const root = document.documentElement;
  while (CSS_PROP_CLEANUP.length) {
    root.style.removeProperty(CSS_PROP_CLEANUP.pop()!);
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Buyer intent modal
// ────────────────────────────────────────────────────────────────────────────

interface BuyerIntentSubmit {
  email: string;
  firstName: string;
  lastName: string;
  plan: 'pif' | '12pay';
}

function BuyerIntentModal(props: {
  open: boolean;
  plan: 'pif' | '12pay';
  programSlug: string;
  pifLabel: string;
  monthlyLabel: string;
  onClose: () => void;
  onSubmit: (data: BuyerIntentSubmit) => void;
}) {
  const { open, plan, pifLabel, monthlyLabel, onClose, onSubmit } = props;
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setFirstName('');
      setLastName('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !firstName) {
      setError('First name and email are required.');
      return;
    }
    setSubmitting(true);
    try {
      onSubmit({ email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim(), plan });
    } catch {
      setError('Something went wrong. Try again, or email Lisa directly.');
      setSubmitting(false);
    }
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <button style={modalCloseBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
        <p style={modalEyebrow}>
          {plan === 'pif' ? pifLabel : monthlyLabel}
        </p>
        <h2 style={modalH2}>Quick — where should we send the order details?</h2>
        <p style={modalSub}>
          We just need your name and email so the order page can pre-fill, and so Lisa
          knows you're on your way. No surprises.
        </p>
        <form onSubmit={handle} style={modalForm}>
          <div style={modalRow}>
            <label style={modalLabel}>
              First name
              <input
                type="text"
                required
                autoFocus
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={modalInput}
              />
            </label>
            <label style={modalLabel}>
              Last name
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={modalInput}
              />
            </label>
          </div>
          <label style={modalLabel}>
            Email
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={modalInput}
            />
          </label>
          {error && <p style={modalError}>{error}</p>}
          <button type="submit" disabled={submitting} style={modalSubmit}>
            {submitting ? 'One sec…' : 'Continue to checkout →'}
          </button>
          <p style={modalFineprint}>
            Continuing takes you to the secure checkout page. Your card is charged through Spiffy
            — not stored here.
          </p>
        </form>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────

export default function ProgramSalesPage() {
  const { slug = 'clients', region: regionParam } = useParams<{ slug: string; region?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Admin/QA currency preview — see sop-ee-clients-multicurrency.md. Forwarded
  // to the public API only when present; a normal visitor's URL never carries
  // it, so this never changes default (IP-resolved) behavior.
  const curParam = searchParams.get('cur');
  const [status, setStatus] = useState<'loading' | 'ready' | 'not-found' | 'error'>('loading');
  const [retryNonce, setRetryNonce] = useState(0);
  const [data, setData] = useState<ProgramPublicResponse | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPlan, setModalPlan] = useState<'pif' | '12pay'>('pif');
  // Live "next breakout" When — resolved from the active cohort's next
  // scheduled session via GET /api/programs/:slug/cohort-stats, the SAME
  // resolver that powers the confirmation-email {{kickoffDateLong}} tokens
  // (see BkosFoundingCohortPage for the reference pattern). NEVER hardcoded:
  // this is what closed the 2026-09-10 gap where the page kept advertising
  // "Next Intake · Wednesday, July 22, 2026" for months after that date
  // passed. Null while loading or on fetch failure — every render path
  // below degrades to date-free "Enrollment open" copy rather than ever
  // showing a stale or wrong date. Framed as the monthly newbie-breakout
  // orientation (decision mcd_282c6fa4-f1a) — enrollment is always open,
  // the breakout date is informational, never a start gate.
  const [kickoffWhen, setKickoffWhen] = useState<string | null>(null);


  // ── Landing-view beacon (once per mount) ────────────────────────────────
  useEffect(() => {
    if (slug) recordLandingView(slug);
  }, [slug]);

  // Brand-aware GTM — waits for the program payload so a PB-branded program
  // (e.g. bkos-license) gets its own container, never the TSB fallback first.
  useBrandGtm(data?.brand?.gtmContainerId, status !== 'loading');

  // ── Fetch program data ──────────────────────────────────────────────────
  // fetchWithRetry absorbs transient first-load failures (dropped first
  // connection, momentary 5xx) so one blip never strands a campaign visitor
  // on the error screen. retryNonce lets the error view offer "Try again"
  // without a full page reload.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStatus('loading');
      try {
        const qp = new URLSearchParams();
        if (regionParam) qp.set('region', regionParam);
        if (curParam) qp.set('cur', curParam);
        const qs = qp.toString() ? `?${qp.toString()}` : '';
        const res = await fetchWithRetry(`${API}/api/public/programs/${encodeURIComponent(slug)}${qs}`);
        if (!res.ok) {
          if (!cancelled) setStatus(res.status === 404 ? 'not-found' : 'error');
          return;
        }
        const json = (await res.json()) as ProgramPublicResponse;
        if (!cancelled) {
          setData(json);
          setStatus('ready');
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, regionParam, curParam, retryNonce]);

  // ── Fetch live breakout date (long-form sales content only) ─────────────
  // Scoped to slugs with SALES_CONTENT (today: clients) rather than firing
  // for every program page — cohort-stats is aggregate-only public data, but
  // there's no reason to fetch it for the bespoke pages, which resolve their
  // own kickoff copy independently (BkosFoundingCohortPage, FreedomGatewayPage).
  useEffect(() => {
    if (!SALES_CONTENT[slug]) return;
    let cancelled = false;
    fetchWithRetry(`${API}/api/programs/${encodeURIComponent(slug)}/cohort-stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        const dateStr = typeof d.kickoffDateLongYear === 'string' && d.kickoffDateLongYear
          ? d.kickoffDateLongYear
          : (typeof d.kickoffDateLong === 'string' && d.kickoffDateLong ? d.kickoffDateLong : null);
        const timeStr = typeof d.kickoffTimeRange === 'string' && d.kickoffTimeRange ? d.kickoffTimeRange : null;
        setKickoffWhen(dateStr ? (timeStr ? `${dateStr} · ${timeStr}` : dateStr) : null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);

  // Geo auto-route: a visitor on the default page whose country maps to a
  // non-default regional cohort is redirected to that region (generalizes the
  // BKOS AU auto-route). Dormant until a program has >1 regional cohort.
  useRegionRedirect(slug, regionParam, data?.regions);

  // ── Apply tokens to DOM ─────────────────────────────────────────────────
  useEffect(() => {
    if (!data) return;
    applyTokensToDom(data);
    return cleanupTokensFromDom;
  }, [data]);

  // ── Load Google Fonts ───────────────────────────────────────────────────
  useEffect(() => {
    if (!data?.googleFontsUrl) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = data.googleFontsUrl;
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, [data?.googleFontsUrl]);

  // ── Document title ──────────────────────────────────────────────────────
  // Use the resolved brand name (e.g. Pure Bookkeeping) rather than the static
  // TSB BRAND_NAME so PB-served program pages read correctly in the browser tab.
  useEffect(() => {
    const brandName = data?.brand?.name ?? BRAND_NAME;
    if (data?.program?.name) {
      document.title = `${data.program.name} — ${brandName}`;
    }
    return () => {
      document.title = brandName;
    };
  }, [data?.program?.name, data?.brand?.name]);

  // ── Brand favicon ───────────────────────────────────────────────────────
  useEffect(() => {
    const url = data?.brand?.faviconUrl;
    if (!url) return;
    const head = document.head;
    let icon = head.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const prev = icon?.href ?? null;
    if (!icon) {
      icon = document.createElement('link');
      icon.rel = 'icon';
      head.appendChild(icon);
    }
    icon.href = url;
    return () => {
      const current = head.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (current) current.href = prev ?? '/favicon.svg';
    };
  }, [data?.brand?.faviconUrl]);

  // ── CTA handlers ────────────────────────────────────────────────────────
  const openPif = () => {
    setModalPlan('pif');
    setModalOpen(true);
  };
  const open12pay = () => {
    setModalPlan('12pay');
    setModalOpen(true);
  };

  const PASSTHROUGH_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'fbclid', 'code',
  ] as const;

  const BUYER_INTENT_SLUGS: Record<string, string> = {
    clients: 'clients-program',
    // FG buyer-intent assessment (create in admin: type='buyer_intent',
    // slug='freedom-gateway-program') — see the go-live runbook.
    'freedom-gateway': 'freedom-gateway-program',
  };

  const handleIntent = async (payload: BuyerIntentSubmit) => {
    const inbound = new URLSearchParams(window.location.search);

    const out = new URLSearchParams({
      plan: payload.plan,
      em: payload.email,
      fn: payload.firstName,
      ln: payload.lastName,
    });
    for (const k of PASSTHROUGH_KEYS) {
      const v = inbound.get(k);
      if (v) out.set(k, v);
    }

    const attribution: Record<string, string> = {};
    for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
      const v = inbound.get(k);
      if (v) attribution[k] = v;
    }

    const intentSlug = BUYER_INTENT_SLUGS[slug];
    if (intentSlug) {
      try {
        const res = await fetch(`${API}/api/buyer-intent/capture`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            slug: intentSlug,
            email: payload.email,
            first_name: payload.firstName,
            last_name: payload.lastName,
            source_page: window.location.pathname + window.location.search,
            attribution,
          }),
        });
        // Deliberately fire-and-forget — checkout must never wait on this.
        // But a swallowed non-ok response was previously invisible even in
        // the browser console, so a missing/unpublished buyer_intent
        // assessment (cart-recovery + attribution's only signal) could go
        // dark with zero symptom. The server logs an integration_event_log
        // breadcrumb on the same failure; this is the client-side echo.
        if (!res.ok) {
          console.warn(`[buyer-intent] capture for '${intentSlug}' failed`, res.status);
        }
      } catch {
        /* swallow — navigation still fires */
      }
    }

    navigate(`/programs/${slug}/order?${out.toString()}`);
  };

  // ── Status states ───────────────────────────────────────────────────────
  if (status === 'loading') {
    return <div style={loadingStyle}>Loading…</div>;
  }
  if (status === 'not-found') {
    return <div style={loadingStyle}>Program not found.</div>;
  }
  if (status === 'error' || !data) {
    return (
      <div style={loadingStyle}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ margin: '0 0 16px' }}>Hmm — the page didn't load that time.</p>
          <button
            type="button"
            onClick={() => setRetryNonce((n) => n + 1)}
            style={{
              padding: '10px 24px',
              borderRadius: 8,
              border: 'none',
              background: '#F05A28',
              color: '#fff',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Membership tiers (Alumni, The Boardroom) render their own sales pages —
  // copy ported from the retired www HubSpot pages.
  if (MEMBERSHIP_CONTENT[slug]) {
    return <MembershipSalesPage slug={slug} data={data} />;
  }

  // SODA Intensive — standalone 1:1 paid offer, its own single-offer sales page
  // (migrated from the HubSpot LP /lp/soda-intensive). Not a cohort, so it gets
  // a dedicated component rather than the Clients long-form template.
  if (slug === 'soda-intensive') {
    return <SodaIntensivePage slug={slug} data={data} />;
  }

  // BKOS License — standalone firm-level recurring subscription ($195/mo) to the
  // bkos.io platform (PB templates). Its own PB-branded sales page; distinct from
  // the free `bkos-founding-cohort` program.
  if (slug === 'bkos-license') {
    return <BkosLicensePage slug={slug} data={data} />;
  }

  // Freedom Gateway — 12-month PB-licensee program. Bespoke emerald/gold landing
  // ported from the HubSpot LP, region-aware (NA / AU) via data.region. Renders
  // a waitlist CTA until the resolved cohort has commerce, then the two-tier
  // (Solo / Ultimate) Spiffy order flow.
  if (slug === 'freedom-gateway') {
    return <FreedomGatewayPage slug={slug} data={data} regionParam={regionParam} />;
  }

  // Programs without a full sales page (RSVP shells — pre_open cohort, no
  // dates or pricing yet) get the branded waitlist view instead of the
  // long-form sales page, which is Clients-only copy for now.
  const c = SALES_CONTENT[slug];
  if (!c) {
    return <ProgramRsvpView slug={slug} data={data} />;
  }

  // Live badge/eyebrow line — "Next breakout" when the date has resolved,
  // else the date-free fallback. Never "Next Intake": enrollment is always
  // open, so nothing here should read as a start gate. Shared by the top
  // nav badge, the eyebrow strip, and the final CTA fineprint.
  const breakoutLine = kickoffWhen ? `Next breakout · ${kickoffWhen}` : c.whenLine;

  // Currency-resolved money for display. `data.commerce` already arrives in the
  // visitor's currency (server-resolved from their IP) — the other currencies
  // never reach the browser, and the parity is never advertised. USD fallback
  // keeps a null-commerce render safe. Numerals are identical across currencies
  // by business decision; only the currency label, tax note, and checkout URL
  // differ per currency.
  const plans = (data.commerce?.plans ?? []) as Array<{
    id?: string; total?: number; per_payment?: number; payments?: number; savings?: number;
  }>;
  const pifPlan = plans.find((p) => p.id === 'clients-pif') ?? plans.find((p) => p.per_payment == null);
  const payPlan = plans.find((p) => p.id === 'clients-12pay') ?? plans.find((p) => p.per_payment != null);
  const cur = (data.commerce?.currency ?? 'USD').toUpperCase();
  const fmt = (n: number | undefined) => (n == null ? '' : `$${n.toLocaleString('en-US')}`);
  const pifTotal = pifPlan?.total ?? 5000;
  const monthlyAmt = payPlan?.per_payment ?? 525;
  const monthlyTotal = payPlan?.total ?? 6300;
  const savingsAmt = pifPlan?.savings ?? (monthlyTotal - pifTotal);
  const money = {
    cur,
    pif: fmt(pifTotal),               // "$5,000"
    monthly: fmt(monthlyAmt),         // "$525"
    monthlyTotal: fmt(monthlyTotal),  // "$6,300"
    savings: fmt(savingsAmt),         // "$1,300"
    taxNote: (data.commerce as { taxNote?: string } | null)?.taxNote ?? `All prices in ${cur}.`,
    pifModalLabel: `Pay in full · ${fmt(pifTotal)} ${cur}`,
    monthlyModalLabel: `12 monthly payments · ${fmt(monthlyAmt)} ${cur}/month`,
  };
  const interp = (s: string) =>
    s.replace(/\{PIF\}/g, money.pif)
      .replace(/\{MONTHLY\}/g, money.monthly)
      .replace(/\{SAVINGS\}/g, money.savings)
      .replace(/\{CUR\}/g, money.cur);

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={pageWrap}>
      <style>{`
        .shift-bullet::before { content: '→'; margin-right: 8px; color: rgba(255,107,53,0.85); font-weight: 700; }
        .journey-bullet-a::before { content: '✓'; margin-right: 8px; color: #FF6B35; font-weight: 700; }
        .journey-bullet-b::before { content: '✓'; margin-right: 8px; color: rgba(255,255,255,0.6); font-weight: 700; }
        .included-bullet::before { content: '✓'; margin-right: 8px; color: #FF6B35; font-weight: 700; }
        .fit-yes::before { content: '✓'; margin-right: 10px; color: #22A55A; font-weight: 700; }
        .fit-no::before { content: '✕'; margin-right: 10px; color: #B84040; font-weight: 600; }
      `}</style>

      {/* ── Top nav ── */}
      <header style={topNav}>
        <div style={topNavInner}>
          {data.brand?.logoUrl && (
            <a href="https://www.thesuccessfulbookkeeper.com" style={topNavLogoLink}>
              <img src={data.brand.logoUrl} alt={data.brand.name} style={topNavLogo} />
            </a>
          )}
          <div style={topNavRight}>
            <span style={topNavMeta}>
              <span style={topNavDot}>●</span> {breakoutLine}
            </span>
            <a href={`/lets-talk/${slug}`} style={topNavLink}>
              Talk with our team
            </a>
          </div>
        </div>
      </header>

      {/* ── Eyebrow strip ── */}
      <div style={eyebrowStrip}>
        <span style={eyebrowDot}>•</span>
        <span style={eyebrowText}>{c.eyebrow}</span>
        <span style={eyebrowSep}>·</span>
        <span style={eyebrowWhen}>{breakoutLine}</span>
      </div>

      {/* ── Hero ── */}
      <section style={heroSection}>
        <HeroBackgroundLayer bg={{ type: 'video', videoUrl: HERO_VIDEO, posterUrl: HERO_POSTER }} />
        <div style={heroInner}>
          {(data.brand?.logoWhiteUrl || data.brand?.logoUrl) && (
            <img
              src={data.brand.logoWhiteUrl ?? data.brand.logoUrl!}
              alt={data.brand.name}
              style={data.brand?.logoWhiteUrl ? heroLogo : heroLogoFiltered}
            />
          )}
          <h1 style={heroH1}>{c.h1}</h1>
          <p style={heroSub}>{c.heroSub}</p>
          <div style={heroCtaRow}>
            <button onClick={openPif} style={primaryCta}>
              Get the program — pay in full
            </button>
            <button onClick={open12pay} style={secondaryCta}>
              Or 12 monthly payments
            </button>
          </div>
          <p style={heroFineprint}>
            {money.pif} {money.cur} pay in full · save {money.savings} vs the monthly plan.
          </p>
          <div style={quickstatsRow}>
            {c.quickstats.map((qs, i) => (
              <div key={i} style={quickstatItem}>
                <strong style={quickstatVal}>{qs.value}</strong>
                {qs.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Problem ── */}
      <section style={sectionPale}>
        <div style={sectionInner}>
          <p style={sectionLabel}>Sound Familiar?</p>
          <h2 style={h2}>Here's what nobody told you when you started your bookkeeping business</h2>
          <p style={lede}>{c.painIntro}</p>
          <ul style={painList}>
            {c.pains.map((p, i) => (
              <li key={i} style={painItem}>
                <span style={painLead}>{p.lead}</span>
                {p.proof && <span style={painProof}>{p.proof}</span>}
              </li>
            ))}
          </ul>
          <p style={painClose}>{c.painClose}</p>
        </div>
      </section>

      {/* ── Three shifts (navy) ── */}
      <section style={sectionNavy}>
        <div style={sectionInner}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabelLight}>The Three Shifts</p>
            <h2 style={h2Inverse}>What changes when you finish Clients</h2>
            <p style={ledeInverse}>Twelve months from now, three things look completely different.</p>
          </div>
          <div style={shiftGrid}>
            {c.shifts.map((s, i) => (
              <div key={i} style={shiftCard}>
                <span style={shiftPhaseTag}>{s.phase}</span>
                <h3 style={shiftCardH3}>{s.h3}</h3>
                <p style={shiftCardP}>{s.body}</p>
                <ul style={shiftBulletList}>
                  {s.bullets.map((b, j) => (
                    <li key={j} style={shiftBulletItem} className="shift-bullet">{b}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 12-month journey ── */}
      <section style={section}>
        <div style={sectionWide}>
          <div style={journeyIntro}>
            <p style={sectionLabel}>How The Program Works</p>
            <h2 style={h2}>Install the system. Then spend twelve months putting it to work.</h2>
            <p style={lede}>
              Most coaching programs end the day the content does. Clients is different. The first
              phase hands you every framework, exercise, and asset you'll need. The remaining months
              are when you actually use them — with the coaching team beside you for every new
              prospect, every discovery call, every awkward pricing conversation.
            </p>
          </div>
          <div style={journeySplit}>
            <div style={journeyBlockA}>
              <p style={journeyTitleA}>{c.journeyInstallTitle}</p>
              <h3 style={journeyHeadlineA}>{c.journeyInstallHeadline}</h3>
              <p style={journeySubA}>{c.journeyInstallSub}</p>
              <ul style={journeyBulletList}>
                {c.journeyInstallBullets.map((b, i) => (
                  <li key={i} style={journeyBulletItemA} className="journey-bullet-a">{b}</li>
                ))}
              </ul>
            </div>
            <div style={journeyBlockB}>
              <p style={journeyTitleB}>{c.journeyExecTitle}</p>
              <h3 style={journeyHeadlineB}>{c.journeyExecHeadline}</h3>
              <p style={journeySubB}>{c.journeyExecSub}</p>
              <ul style={journeyBulletList}>
                {c.journeyExecBullets.map((b, i) => (
                  <li key={i} style={journeyBulletItemB} className="journey-bullet-b">{b}</li>
                ))}
              </ul>
            </div>
          </div>
          <div style={journeyCalloutBox}>
            <p style={journeyCalloutText}>
              Idea + strategy doesn't fry the fish. You need{' '}
              <strong style={{ color: PROG_PRIMARY }}>execution and accountability</strong> — and
              that's exactly what the next twelve months deliver.
            </p>
          </div>
        </div>
      </section>

      {/* ── Team ── */}
      <section style={sectionPale}>
        <div style={sectionWide}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>Your Team</p>
            <h2 style={h2}>You're not doing this alone</h2>
            <p style={{ ...lede, maxWidth: '640px', margin: '0 auto 48px' }}>
              Six experts. Six different superpowers. Each one is a specialist most bookkeepers
              would pay thousands to work with individually. They're all in your corner for the
              full 12 months.
            </p>
          </div>
          <div style={teamGrid}>
            {c.team.map((person, i) => (
              <div key={i} style={teamCard}>
                <div style={teamPhotoWrap}>
                  <img src={person.photo} alt={person.name} style={teamPhotoImg} />
                </div>
                <p style={teamRole}>{person.role}</p>
                <p style={teamName}>{person.name}</p>
                <p style={teamBio}>{person.bio}</p>
              </div>
            ))}
          </div>
          <p style={teamCallout}>
            If you had to hire this team individually, it would cost more than the program. You
            don't have to — they come with it.
          </p>
        </div>
      </section>

      {/* ── Curriculum ── */}
      <section style={sectionCream}>
        <div style={sectionWide}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>The Curriculum</p>
            <h2 style={h2}>The 12 modules that build your client acquisition system</h2>
            <p style={{ ...lede, maxWidth: '640px', margin: '0 auto 48px' }}>
              Each module is delivered live, one per week, with Lisa as host. Workbooks, exercises,
              and templates included. Recordings available for life.
            </p>
          </div>
          <div style={curriculumGrid}>
            {c.modules.map((m, i) => (
              <div key={i} style={moduleCard}>
                <div style={moduleHead}>
                  <span style={moduleNum}>{m.num}</span>
                  <span style={modulePhase}>{m.phase}</span>
                </div>
                <p style={moduleTitle}>{m.title}</p>
                <p style={moduleDesc}>{m.desc}</p>
              </div>
            ))}
          </div>
          <div style={curriculumStrip}>
            {c.moduleStrip.map((s, i) => (
              <div key={i} style={stripItem}>
                <div style={stripNum}>{s.num}</div>
                <div style={stripLabel}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── What's included ── */}
      <section style={section}>
        <div style={sectionWide}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>Everything That's In</p>
            <h2 style={h2}>What you get for 12 full months</h2>
            <p style={{ ...lede, maxWidth: '560px', margin: '0 auto 48px' }}>
              Coaching is the engine. The content is the fuel. The team is what gets you there.
            </p>
          </div>
          <div style={includedGrid}>
            {c.includedCards.map((card, i) => (
              <div key={i} style={includedCard}>
                <span style={includedCardTag}>{card.tag}</span>
                <h3 style={includedCardH3}>{card.h3}</h3>
                <p style={includedCardP}>{card.body}</p>
                {card.bullets && (
                  <ul style={includedBulletList}>
                    {card.bullets.map((b, j) => (
                      <li key={j} style={includedBulletItem} className="included-bullet">{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Is this right for you ── */}
      <section style={section}>
        <div style={sectionInner}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>Is This Right For You?</p>
            <h2 style={h2}>If any of this sounds like you, you're in the right place</h2>
          </div>
          <div style={fitGrid}>
            <div>
              <h3 style={fitColH3}>This is for you if you want…</h3>
              <ul style={fitList}>
                {c.fitYes.map((item, i) => (
                  <li key={i} style={fitItemYes} className="fit-yes">{item}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 style={fitColH3}>This isn't for you if…</h3>
              <ul style={fitList}>
                {c.fitNo.map((item, i) => (
                  <li key={i} style={fitItemNo} className="fit-no">{item}</li>
                ))}
              </ul>
            </div>
          </div>
          <p style={fitClose}>{c.fitClose}</p>
        </div>
      </section>

      {/* ── Stats strip ── */}
      <section style={sectionTint}>
        <div style={sectionWide}>
          <div style={statsGrid}>
            {c.stats.map((s, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={statNum}>{s.num}</div>
                <div style={statLabel}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Lisa letter ── */}
      <section style={section}>
        <div style={sectionWide}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <p style={sectionLabel}>{c.lisaLabel}</p>
            <h2 style={h2}>{c.lisaH2}</h2>
          </div>
          <div style={lisaGrid}>
            <div style={lisaPhotoContainer}>
              <img src={c.lisaPhoto} alt="Lisa Campbell" style={lisaPhotoImg} />
            </div>
            <div>
              {c.lisaCopy.map((para, i) => (
                <p key={i} style={lisaPara}>{para}</p>
              ))}
              <div style={lisaCredsRow}>
                {c.lisaCreds.map((cred, i) => (
                  <span key={i} style={lisaCredPill}>{cred}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section style={section} id="enrol">
        <div style={sectionInner}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>Your Investment</p>
            <h2 style={h2}>What it costs to stop leaving clients on the table</h2>
          </div>
          <div style={pricingContextBox}>
            <p style={pricingContextText}>{c.pricingContext}</p>
          </div>
          <div style={pricingGrid2}>
            {/* PIF card */}
            <div style={pifCard}>
              <div style={pricingRibbon}>Save {money.savings}</div>
              <div style={pricingCardInner}>
                <p style={pricingPlanLabel}>Pay in full</p>
                <p style={pricingAmountLarge}>{money.pif} <span style={pricingAmountUnit}>{money.cur}</span></p>
                <p style={pricingSubNote}>One payment. Save {money.savings}.</p>
                <hr style={pricingDivider} />
                <p style={pricingIncludesLabel}>Everything included</p>
                <ul style={pricingIncludesList}>
                  {c.pricingIncludes.map((item, i) => (
                    <li key={i} style={pricingIncludesItem}>{item}</li>
                  ))}
                </ul>
                <button onClick={openPif} style={{ ...primaryCta, width: '100%', marginTop: '8px', display: 'block', textAlign: 'center' as const }}>
                  Get the program — pay in full
                </button>
              </div>
            </div>
            {/* 12-pay card */}
            <div style={payCard}>
              <div style={{ height: '24px' }} />
              <p style={pricingPlanLabel}>12 monthly payments</p>
              <p style={pricingAmountMid}>{money.monthly} <span style={pricingAmountUnit}>{money.cur}/mo</span></p>
              <p style={pricingSubNote}>{money.monthlyTotal} total over 12 months.</p>
              <hr style={{ ...pricingDivider, borderColor: 'rgba(255,107,53,0.12)' }} />
              <p style={payCardNote}>
                Same program, same access, same results — everything listed to the left is included.
              </p>
              <button onClick={open12pay} style={{ ...secondaryCtaLight, width: '100%', display: 'block', textAlign: 'center' as const }}>
                Get the program — 12 monthly payments
              </button>
            </div>
          </div>
          <p style={pricingNote}>{money.taxNote}</p>
        </div>
      </section>

      {/* ── Downsell ── */}
      <section style={downsellSection}>
        <div style={sectionInner}>
          <h3 style={downsellH3}>Not quite ready to commit?</h3>
          <p style={downsellP}>
            No pressure. If you want to talk through whether the Clients program is the right fit
            for where you are, our team is here to help.
          </p>
          <a href={`/lets-talk/${slug}`} style={downsellBtn}>
            Talk with our team about your next step
          </a>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={section}>
        <div style={sectionInner}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>Common Questions</p>
            <h2 style={h2}>{c.faqs.length > 0 ? 'Everything you need to know' : ''}</h2>
          </div>
          <div style={faqList}>
            {c.faqs.map((f, i) => (
              <details key={i} style={faqItem}>
                <summary style={faqQ}>{f.q}</summary>
                <p style={faqA}>{interp(f.a)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section style={finalSection}>
        <div style={sectionInner}>
          <h2 style={h2Inverse}>{c.finalHeadline}</h2>
          <p style={ledeInverse}>
            {c.finalSub}
            {kickoffWhen ? ` Your newbie breakout — a live orientation with the team — is ${kickoffWhen}.` : ''}
          </p>
          <div style={heroCtaRow}>
            <button onClick={openPif} style={primaryCta}>
              Get the program — pay in full
            </button>
            <button onClick={open12pay} style={secondaryCta}>
              Or 12 monthly payments
            </button>
          </div>
          <p style={{ ...heroFineprint, marginTop: '20px' }}>
            {breakoutLine} · {money.pif} {money.cur} pay in full or {money.monthly} {money.cur}/month · 12-Month Program
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={footer}>
        <p style={footerMeta}>{interp(c.footerMeta)}</p>
        <p style={footerLink}>
          Not quite ready?{' '}
          <a href={`/lets-talk/${slug}`} style={footerAnchor}>
            Talk with our team instead
          </a>
        </p>
        <div style={{ marginTop: 8 }}><PolicyLinks /></div>
      </footer>

      <BuyerIntentModal
        open={modalOpen}
        plan={modalPlan}
        programSlug={slug}
        pifLabel={money.pifModalLabel}
        monthlyLabel={money.monthlyModalLabel}
        onClose={() => setModalOpen(false)}
        onSubmit={handleIntent}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Inline styles
// ────────────────────────────────────────────────────────────────────────────

const PROG_PRIMARY = 'var(--prog-primary, #FF6B35)';
const PROG_BRIGHT = 'var(--prog-bright, #FF8855)';
const PROG_DEEP = 'var(--prog-deep, #1A1A3E)';
const PROG_TEXT_DARK = 'var(--prog-text-dark, #0A0A2E)';
const PROG_TINT = 'var(--prog-tint, #FFF7F4)';
const PROG_HIGHLIGHT = 'var(--prog-highlight, #F5C300)';
const FONT_HEADING = 'var(--font-heading, "Fraunces", Georgia, serif)';
const FONT_BODY = 'var(--font-body, "Poppins", -apple-system, sans-serif)';

const pageWrap: React.CSSProperties = {
  background: '#FFFFFF',
  color: PROG_TEXT_DARK,
  fontFamily: FONT_BODY,
  fontSize: '17px',
  lineHeight: 1.6,
};

const loadingStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  fontFamily: FONT_BODY,
  color: PROG_TEXT_DARK,
};

// Top nav
const topNav: React.CSSProperties = {
  background: '#FFFFFF',
  borderBottom: '1px solid rgba(10,10,46,0.08)',
  padding: '12px 24px',
  position: 'sticky',
  top: 0,
  zIndex: 50,
};
const topNavInner: React.CSSProperties = {
  maxWidth: '1100px',
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
};
const topNavLogoLink: React.CSSProperties = { display: 'inline-flex', alignItems: 'center' };
const topNavLogo: React.CSSProperties = { height: '36px', width: 'auto', display: 'block' };
const topNavRight: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '20px' };
const topNavMeta: React.CSSProperties = { fontFamily: FONT_BODY, fontSize: '13px', color: PROG_TEXT_DARK, opacity: 0.8 };
const topNavDot: React.CSSProperties = { color: PROG_PRIMARY, fontSize: '10px', marginRight: '6px' };
const topNavLink: React.CSSProperties = {
  fontFamily: FONT_BODY,
  fontSize: '13px',
  fontWeight: 600,
  color: PROG_PRIMARY,
  textDecoration: 'none',
  borderBottom: `2px solid ${PROG_PRIMARY}`,
  paddingBottom: '1px',
};

// Eyebrow
const eyebrowStrip: React.CSSProperties = {
  background: PROG_DEEP,
  color: '#fff',
  fontFamily: FONT_BODY,
  fontSize: '13px',
  letterSpacing: '0.04em',
  padding: '10px 24px',
  textAlign: 'center',
};
const eyebrowDot: React.CSSProperties = { color: PROG_PRIMARY, marginRight: '6px' };
const eyebrowText: React.CSSProperties = { fontWeight: 600 };
const eyebrowSep: React.CSSProperties = { opacity: 0.6, margin: '0 10px' };
const eyebrowWhen: React.CSSProperties = { opacity: 0.85 };

// Hero background video assets (served from taker/public at the site root).
const HERO_VIDEO = '/tsb-clients-hero-bg.mp4';
const HERO_POSTER = '/tsb-clients-hero-bg-poster.jpg';

// Hero — position:relative + overflow:hidden so HeroBackgroundLayer can fill it.
const heroSection: React.CSSProperties = {
  position: 'relative',
  overflow: 'hidden',
  background: PROG_DEEP,
  color: '#fff',
  padding: '88px 24px 96px',
};
const heroInner: React.CSSProperties = { position: 'relative', zIndex: 2, maxWidth: '880px', margin: '0 auto', textAlign: 'center' };
const heroLogo: React.CSSProperties = { maxHeight: '52px', marginBottom: '28px' };
const heroLogoFiltered: React.CSSProperties = { ...heroLogo, filter: 'brightness(0) invert(1)' };
const heroH1: React.CSSProperties = {
  fontFamily: FONT_HEADING,
  fontSize: 'clamp(36px, 5.5vw, 56px)',
  fontWeight: 800,
  lineHeight: 1.12,
  letterSpacing: '-0.02em',
  margin: '0 0 22px',
};
const heroSub: React.CSSProperties = { fontSize: '19px', lineHeight: 1.55, color: 'rgba(255,255,255,0.82)', margin: '0 0 32px', fontWeight: 300 };
const heroCtaRow: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: '14px', justifyContent: 'center', marginBottom: '16px' };
const heroFineprint: React.CSSProperties = { fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginTop: '14px' };

// Hero quickstats
const quickstatsRow: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '24px',
  justifyContent: 'center',
  marginTop: '36px',
  paddingTop: '28px',
  borderTop: '1px solid rgba(255,255,255,0.08)',
};
const quickstatItem: React.CSSProperties = { fontSize: '13px', color: 'rgba(255,255,255,0.55)' };
const quickstatVal: React.CSSProperties = {
  display: 'block',
  fontSize: '22px',
  color: '#fff',
  fontWeight: 700,
  lineHeight: 1.1,
  marginBottom: '2px',
};

// CTAs
const primaryCta: React.CSSProperties = {
  background: PROG_PRIMARY,
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  padding: '16px 32px',
  fontFamily: FONT_BODY,
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'background .15s ease',
};
const secondaryCta: React.CSSProperties = {
  background: 'transparent',
  color: '#fff',
  border: `2px solid ${PROG_PRIMARY}`,
  borderRadius: '8px',
  padding: '14px 28px',
  fontFamily: FONT_BODY,
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
};
const secondaryCtaLight: React.CSSProperties = {
  ...secondaryCta,
  color: PROG_PRIMARY,
};

// Section layouts
const section: React.CSSProperties = { padding: '80px 24px' };
const sectionPale: React.CSSProperties = { padding: '80px 24px', background: '#FFF3EE' };
const sectionTint: React.CSSProperties = { padding: '64px 24px', background: PROG_TINT };
const sectionCream: React.CSSProperties = { padding: '80px 24px', background: '#FFF7F4' };
const sectionNavy: React.CSSProperties = {
  padding: '80px 24px',
  background: `linear-gradient(160deg, ${PROG_DEEP} 0%, #0D0D38 100%)`,
  color: '#fff',
};
const finalSection: React.CSSProperties = {
  padding: '96px 24px',
  background: `linear-gradient(160deg, ${PROG_DEEP} 0%, #0D0D38 100%)`,
  color: '#fff',
  textAlign: 'center',
};

const sectionInner: React.CSSProperties = { maxWidth: '800px', margin: '0 auto' };
const sectionWide: React.CSSProperties = { maxWidth: '1080px', margin: '0 auto' };

// Section labels
const sectionLabel: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: PROG_PRIMARY,
  marginBottom: '12px',
};
const sectionLabelLight: React.CSSProperties = { ...sectionLabel, color: 'rgba(255,255,255,0.5)' };

// Headings
const h2: React.CSSProperties = {
  fontFamily: FONT_HEADING,
  fontSize: 'clamp(28px, 3.5vw, 38px)',
  fontWeight: 700,
  lineHeight: 1.2,
  color: PROG_TEXT_DARK,
  letterSpacing: '-0.015em',
  margin: '0 0 18px',
};
const h2Inverse: React.CSSProperties = { ...h2, color: '#fff' };

const lede: React.CSSProperties = {
  fontSize: '18px',
  lineHeight: 1.75,
  color: '#3A3A5C',
  fontWeight: 300,
  margin: '0 0 26px',
};
const ledeInverse: React.CSSProperties = { ...lede, color: 'rgba(255,255,255,0.65)' };

// Pain section
const painList: React.CSSProperties = { listStyle: 'none', margin: '40px 0', padding: 0, display: 'flex', flexDirection: 'column', gap: '18px' };
const painItem: React.CSSProperties = {
  fontSize: '18px',
  fontWeight: 400,
  lineHeight: 1.6,
  paddingLeft: '28px',
  position: 'relative',
  color: PROG_TEXT_DARK,
};
const painLead: React.CSSProperties = { display: 'block' };
const painProof: React.CSSProperties = {
  display: 'block',
  marginTop: '10px',
  fontSize: '14px',
  fontStyle: 'italic',
  color: '#3A3A5C',
  padding: '10px 14px',
  background: 'rgba(255,107,53,0.06)',
  borderLeft: '3px solid rgba(255,107,53,0.3)',
  borderRadius: '0 6px 6px 0',
  lineHeight: 1.6,
};
const painClose: React.CSSProperties = {
  fontWeight: 600,
  fontSize: '18px',
  color: PROG_TEXT_DARK,
  marginTop: '40px',
  paddingTop: '32px',
  borderTop: '2px solid rgba(255,107,53,0.15)',
};

// Shifts (navy)
const shiftGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '24px',
  marginTop: '48px',
};
const shiftCard: React.CSSProperties = {
  padding: '36px 28px',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: '22px',
};
const shiftPhaseTag: React.CSSProperties = {
  display: 'inline-block',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: PROG_PRIMARY,
  background: 'rgba(255,107,53,0.12)',
  padding: '4px 10px',
  borderRadius: '4px',
  marginBottom: '14px',
};
const shiftCardH3: React.CSSProperties = {
  fontFamily: FONT_HEADING,
  fontSize: '22px',
  fontWeight: 600,
  color: '#fff',
  margin: '0 0 12px',
};
const shiftCardP: React.CSSProperties = { fontSize: '15px', lineHeight: 1.7, color: 'rgba(255,255,255,0.65)', marginBottom: '16px' };
const shiftBulletList: React.CSSProperties = { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' };
const shiftBulletItem: React.CSSProperties = {
  fontSize: '14px',
  lineHeight: 1.5,
  color: 'rgba(255,255,255,0.75)',
};

// Journey
const journeyIntro: React.CSSProperties = { textAlign: 'center', maxWidth: '640px', margin: '0 auto 56px' };
const journeySplit: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '24px',
  alignItems: 'stretch',
};
const journeyBlockA: React.CSSProperties = {
  padding: '40px 32px',
  borderRadius: '22px',
  background: 'linear-gradient(160deg, #FFF3EE 0%, #FFE4D5 100%)',
  border: '1.5px solid rgba(255,107,53,0.18)',
};
const journeyBlockB: React.CSSProperties = {
  padding: '40px 32px',
  borderRadius: '22px',
  background: `linear-gradient(160deg, ${PROG_DEEP} 0%, #1A1A4A 100%)`,
  border: '1.5px solid rgba(255,107,53,0.18)',
  color: '#fff',
};
const journeyTitleA: React.CSSProperties = {
  fontSize: '13px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
  color: PROG_PRIMARY, marginBottom: '8px',
};
const journeyTitleB: React.CSSProperties = { ...journeyTitleA, color: PROG_HIGHLIGHT };
const journeyHeadlineA: React.CSSProperties = {
  fontFamily: FONT_HEADING, fontSize: 'clamp(24px, 3vw, 30px)', fontWeight: 800,
  lineHeight: 1.15, letterSpacing: '-0.015em', margin: '0 0 14px', color: PROG_TEXT_DARK,
};
const journeyHeadlineB: React.CSSProperties = { ...journeyHeadlineA, color: '#fff' };
const journeySubA: React.CSSProperties = { fontSize: '15px', lineHeight: 1.7, marginBottom: '22px', color: '#3A3A5C' };
const journeySubB: React.CSSProperties = { ...journeySubA, color: 'rgba(255,255,255,0.75)' };
const journeyBulletList: React.CSSProperties = { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' };
const journeyBulletItemA: React.CSSProperties = { fontSize: '14.5px', lineHeight: 1.6, color: PROG_TEXT_DARK };
const journeyBulletItemB: React.CSSProperties = { ...journeyBulletItemA, color: 'rgba(255,255,255,0.85)' };
const journeyCalloutBox: React.CSSProperties = {
  marginTop: '48px',
  textAlign: 'center',
  maxWidth: '640px',
  marginLeft: 'auto',
  marginRight: 'auto',
  padding: '24px 28px',
  background: PROG_TINT,
  border: '1px solid rgba(255,107,53,0.15)',
  borderRadius: '22px',
};
const journeyCalloutText: React.CSSProperties = { fontSize: '17px', lineHeight: 1.65, color: PROG_TEXT_DARK, fontWeight: 500, margin: 0 };

// Team
const teamGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '24px',
  marginTop: '0',
};
const teamCard: React.CSSProperties = {
  background: '#fff',
  borderRadius: '22px',
  padding: '28px 24px',
  border: '1px solid rgba(255,107,53,0.08)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
};
const teamPhotoWrap: React.CSSProperties = {
  width: '84px',
  height: '84px',
  borderRadius: '50%',
  overflow: 'hidden',
  border: '2px solid rgba(255,107,53,0.20)',
  marginBottom: '16px',
};
const teamPhotoImg: React.CSSProperties = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' };
const teamRole: React.CSSProperties = {
  fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
  color: PROG_PRIMARY, marginBottom: '6px',
};
const teamName: React.CSSProperties = {
  fontFamily: FONT_HEADING, fontSize: '18px', fontWeight: 700, color: PROG_TEXT_DARK,
  marginBottom: '10px', lineHeight: 1.2,
};
const teamBio: React.CSSProperties = { fontSize: '14px', lineHeight: 1.6, color: '#3A3A5C', margin: 0 };
const teamCallout: React.CSSProperties = {
  textAlign: 'center', marginTop: '40px', fontSize: '15px', color: '#3A3A5C',
  fontStyle: 'italic', maxWidth: '580px', marginLeft: 'auto', marginRight: 'auto',
};

// Curriculum
const curriculumGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '16px',
  marginTop: '0',
};
const moduleCard: React.CSSProperties = {
  background: 'rgba(255,107,53,0.06)',
  border: '1px solid rgba(255,107,53,0.18)',
  borderRadius: '14px',
  padding: '22px 24px',
};
const moduleHead: React.CSSProperties = { display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '8px' };
const moduleNum: React.CSSProperties = { fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', color: PROG_PRIMARY };
const modulePhase: React.CSSProperties = {
  fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
  color: 'rgba(245,195,0,0.85)', padding: '2px 8px', background: 'rgba(245,195,0,0.10)', borderRadius: '4px',
};
const moduleTitle: React.CSSProperties = { fontSize: '16px', fontWeight: 700, color: PROG_TEXT_DARK, marginBottom: '6px', lineHeight: 1.3 };
const moduleDesc: React.CSSProperties = { fontSize: '13.5px', lineHeight: 1.55, color: '#3A3A5C', margin: 0 };
const curriculumStrip: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '12px',
  marginTop: '40px',
  padding: '24px',
  background: 'rgba(255,107,53,0.05)',
  border: '1px solid rgba(255,107,53,0.18)',
  borderRadius: '22px',
};
const stripItem: React.CSSProperties = { textAlign: 'center' };
const stripNum: React.CSSProperties = { fontSize: '22px', fontWeight: 800, color: PROG_PRIMARY, lineHeight: 1 };
const stripLabel: React.CSSProperties = { fontSize: '12px', color: '#3A3A5C', marginTop: '6px', lineHeight: 1.4 };

// Included cards
const includedGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '24px',
};
const includedCard: React.CSSProperties = {
  padding: '32px 28px',
  background: PROG_TINT,
  borderRadius: '22px',
  border: '1px solid rgba(255,107,53,0.10)',
};
const includedCardTag: React.CSSProperties = {
  display: 'inline-block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em',
  textTransform: 'uppercase', color: PROG_PRIMARY, background: 'rgba(255,107,53,0.10)',
  padding: '4px 10px', borderRadius: '4px', marginBottom: '14px',
};
const includedCardH3: React.CSSProperties = {
  fontFamily: FONT_HEADING, fontSize: '20px', fontWeight: 600, color: PROG_TEXT_DARK,
  margin: '0 0 10px', lineHeight: 1.25,
};
const includedCardP: React.CSSProperties = { fontSize: '14.5px', lineHeight: 1.7, color: '#3A3A5C', margin: 0 };
const includedBulletList: React.CSSProperties = { listStyle: 'none', padding: 0, marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '9px' };
const includedBulletItem: React.CSSProperties = {
  fontSize: '14px', color: '#3A3A5C', lineHeight: 1.55,
};

// Fit
const fitGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', marginTop: '48px' };
const fitColH3: React.CSSProperties = { fontFamily: FONT_HEADING, fontSize: '20px', fontWeight: 600, color: PROG_TEXT_DARK, marginBottom: '20px' };
const fitList: React.CSSProperties = { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' };
const fitItemYes: React.CSSProperties = { fontSize: '15px', lineHeight: 1.65, color: PROG_TEXT_DARK };
const fitItemNo: React.CSSProperties = { fontSize: '15px', lineHeight: 1.65, color: '#3A3A5C' };
const fitClose: React.CSSProperties = {
  fontFamily: FONT_HEADING, fontSize: '17px', lineHeight: 1.65, color: '#3A3A5C',
  fontStyle: 'italic', textAlign: 'center', maxWidth: '580px', margin: '48px auto 0',
};

// Stats
const statsGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '32px',
  textAlign: 'center',
};
const statNum: React.CSSProperties = {
  fontSize: 'clamp(32px, 4vw, 42px)', fontWeight: 900, color: PROG_PRIMARY, lineHeight: 1.1,
};
const statLabel: React.CSSProperties = { fontSize: '13px', fontWeight: 400, color: '#3A3A5C', marginTop: '6px', lineHeight: 1.45 };

// Lisa letter
const lisaGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '260px 1fr',
  gap: '52px',
  alignItems: 'flex-start',
};
const lisaPhotoContainer: React.CSSProperties = {
  borderRadius: '22px',
  overflow: 'hidden',
  border: '1.5px solid rgba(255,107,53,0.15)',
  aspectRatio: '3/4',
};
const lisaPhotoImg: React.CSSProperties = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' };
const lisaPara: React.CSSProperties = { fontSize: '15.5px', color: '#3A3A5C', lineHeight: 1.75, marginBottom: '16px' };
const lisaCredsRow: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '24px' };
const lisaCredPill: React.CSSProperties = {
  fontSize: '12px', fontWeight: 600, color: '#3A3A5C', background: PROG_TINT,
  border: '1px solid rgba(255,107,53,0.10)', borderRadius: '100px', padding: '6px 14px',
};

// Pricing
const pricingContextBox: React.CSSProperties = {
  background: 'rgba(255,107,53,0.07)',
  border: '1px solid rgba(255,107,53,0.18)',
  borderRadius: '16px',
  padding: '28px 32px',
  margin: '32px 0 44px',
};
const pricingContextText: React.CSSProperties = { fontSize: '16px', lineHeight: 1.8, color: '#3A3A5C', margin: 0 };
const pricingGrid2: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1.55fr 1fr',
  gap: '20px',
  alignItems: 'start',
};
const pifCard: React.CSSProperties = {
  background: '#FFFFFF',
  borderRadius: '20px',
  position: 'relative',
  border: `2px solid ${PROG_PRIMARY}`,
  boxShadow: '0 4px 32px rgba(255,107,53,0.10)',
};
const payCard: React.CSSProperties = {
  background: PROG_TINT,
  borderRadius: '20px',
  padding: '36px 28px 32px',
  border: '2px solid rgba(255,107,53,0.15)',
};
const pricingRibbon: React.CSSProperties = {
  position: 'absolute',
  top: '-16px',
  left: '32px',
  background: PROG_PRIMARY,
  color: '#fff',
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  padding: '6px 16px',
  borderRadius: '100px',
  fontFamily: FONT_BODY,
};
const pricingCardInner: React.CSSProperties = { padding: '40px 36px 36px' };
const pricingPlanLabel: React.CSSProperties = { fontSize: '13px', fontWeight: 600, color: '#3A3A5C', marginBottom: '10px' };
const pricingAmountLarge: React.CSSProperties = {
  fontSize: 'clamp(38px, 4vw, 52px)', fontWeight: 900, color: PROG_PRIMARY,
  lineHeight: 1.05, letterSpacing: '-0.02em', marginBottom: '6px',
};
const pricingAmountMid: React.CSSProperties = { ...pricingAmountLarge, fontSize: 'clamp(32px, 3.5vw, 44px)' };
const pricingAmountUnit: React.CSSProperties = { fontSize: '1.1rem', fontWeight: 600 };
const pricingSubNote: React.CSSProperties = { fontSize: '14px', color: '#3A3A5C', marginBottom: '24px' };
const pricingDivider: React.CSSProperties = { border: 'none', borderTop: '1px solid rgba(0,0,0,0.08)', margin: '0 0 20px' };
const pricingIncludesLabel: React.CSSProperties = {
  fontSize: '11px', fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase',
  color: 'rgba(10,10,46,0.35)', marginBottom: '14px',
};
const pricingIncludesList: React.CSSProperties = { listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: '12px' };
const pricingIncludesItem: React.CSSProperties = { fontSize: '15px', paddingLeft: '28px', position: 'relative', lineHeight: 1.55, color: PROG_TEXT_DARK };
const payCardNote: React.CSSProperties = { fontSize: '13.5px', color: '#3A3A5C', lineHeight: 1.65, marginBottom: '24px', fontStyle: 'italic' };
const pricingNote: React.CSSProperties = { textAlign: 'center', fontSize: '13px', color: '#3A3A5C', marginTop: '24px', lineHeight: 1.65 };

// Downsell
const downsellSection: React.CSSProperties = { background: '#FFF3EE', padding: '56px 24px', textAlign: 'center' };
const downsellH3: React.CSSProperties = { fontFamily: FONT_HEADING, fontSize: '22px', fontWeight: 600, color: PROG_TEXT_DARK, marginBottom: '10px' };
const downsellP: React.CSSProperties = { fontSize: '15px', color: '#3A3A5C', maxWidth: '500px', margin: '0 auto 24px' };
const downsellBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '8px',
  fontFamily: FONT_BODY, fontWeight: 600, fontSize: '16px',
  padding: '14px 28px', borderRadius: '8px', border: `2px solid ${PROG_PRIMARY}`,
  color: PROG_PRIMARY, background: 'transparent', textDecoration: 'none', cursor: 'pointer',
};

// FAQ
const faqList: React.CSSProperties = { marginTop: '48px', display: 'flex', flexDirection: 'column', gap: '2px' };
const faqItem: React.CSSProperties = {
  background: PROG_TINT,
  borderRadius: '8px',
  overflow: 'hidden',
};
const faqQ: React.CSSProperties = {
  fontFamily: FONT_HEADING,
  fontSize: '16px',
  fontWeight: 600,
  color: PROG_TEXT_DARK,
  cursor: 'pointer',
  listStyle: 'none',
  padding: '20px 24px',
};
const faqA: React.CSSProperties = {
  fontSize: '15px',
  lineHeight: 1.7,
  color: '#3A3A5C',
  margin: '0',
  padding: '0 24px 20px',
};

// Footer
const footer: React.CSSProperties = {
  background: '#06061A',
  color: 'rgba(255,255,255,0.30)',
  padding: '40px 24px',
  textAlign: 'center',
  fontSize: '13px',
};
const footerMeta: React.CSSProperties = { margin: '0 0 8px' };
const footerLink: React.CSSProperties = { margin: 0 };
const footerAnchor: React.CSSProperties = { color: PROG_BRIGHT, textDecoration: 'underline' };

// Modal styles
const modalOverlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(10,10,46,0.78)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px',
};
const modalCard: React.CSSProperties = {
  background: '#fff',
  color: PROG_TEXT_DARK,
  borderRadius: '12px',
  padding: '36px 32px 28px',
  maxWidth: '480px',
  width: '100%',
  position: 'relative',
  boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
  fontFamily: FONT_BODY,
};
const modalCloseBtn: React.CSSProperties = {
  position: 'absolute',
  top: '12px',
  right: '14px',
  background: 'transparent',
  border: 'none',
  fontSize: '24px',
  color: PROG_TEXT_DARK,
  cursor: 'pointer',
  opacity: 0.5,
};
const modalEyebrow: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: PROG_PRIMARY,
  margin: '0 0 8px',
};
const modalH2: React.CSSProperties = {
  fontFamily: FONT_HEADING,
  fontSize: '22px',
  fontWeight: 600,
  lineHeight: 1.25,
  margin: '0 0 10px',
};
const modalSub: React.CSSProperties = {
  fontSize: '14px',
  lineHeight: 1.5,
  color: PROG_TEXT_DARK,
  opacity: 0.75,
  margin: '0 0 18px',
};
const modalForm: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '12px' };
const modalRow: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' };
const modalLabel: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  fontSize: '12px',
  fontWeight: 600,
  color: PROG_TEXT_DARK,
  letterSpacing: '0.02em',
};
const modalInput: React.CSSProperties = {
  marginTop: '4px',
  padding: '11px 12px',
  fontSize: '15px',
  border: '1px solid #D1D1D6',
  borderRadius: '6px',
  fontFamily: FONT_BODY,
  color: PROG_TEXT_DARK,
};
const modalError: React.CSSProperties = { color: '#B91C1C', fontSize: '13px', margin: '4px 0 0' };
const modalSubmit: React.CSSProperties = { ...primaryCta, width: '100%', marginTop: '6px' };
const modalFineprint: React.CSSProperties = {
  fontSize: '12px',
  color: PROG_TEXT_DARK,
  opacity: 0.6,
  textAlign: 'center',
  margin: '4px 0 0',
};
